import React, { useState, useEffect, useRef, useContext, useLayoutEffect } from 'react';
import io from 'socket.io-client';
import './SocialHiveChat.css';
import { IoAddCircleSharp, IoFileTrayOutline, IoDocumentTextOutline, IoImageOutline, IoVideocamOutline, IoMusicalNotesOutline, IoCodeSlashOutline, IoClose } from 'react-icons/io5';
import { TbChecks, TbPlayerPlayFilled, TbSend2, TbStar, TbStarFilled, TbFileTypePdf, TbFileTypeXls, TbFileTypeTxt, TbFileTypeZip, TbPlus, TbTag, TbTrash } from 'react-icons/tb';
import { GeneralContext } from '../../../../GeneralProvider';
import Services from '../../../../services/Services';
import { useLanguage } from '../../../../LanguageProvider';
import AlertBox from '../../../../components/AlertBox/AlertBox';
import Modal from '../../../../components/Modal/Modal';
import DragDropOverlay from '../../components/DragDropOverlay/DragDropOverlay';
import previewPlaceholder from '../../../../assets/images/preview-bg.webp';
const linkifyText = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) =>
        urlRegex.test(part)
            ? <a key={i} style={{ color: 'var(--sh-chat-message-font-color)', cursor: 'var(--pointer)' }} href={part} target="_blank" rel="noopener noreferrer">{part}</a>
            : part
    );
};

const MAX_FILES = 10;
const MAX_TOTAL_SIZE = 100 * 1024 * 1024;

// File type icon mapping
const getFileIcon = (fileName, mimeType) => {
    const extension = fileName.split('.').pop()?.toLowerCase();

    // Image files
    if (mimeType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
        return <IoImageOutline size={32} />;
    }

    // Video files
    if (mimeType?.startsWith('video/') || ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
        return <IoVideocamOutline size={32} />;
    }

    // Audio files
    if (mimeType?.startsWith('audio/') || ['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension)) {
        return <IoMusicalNotesOutline size={32} />;
    }

    // PDF files
    if (extension === 'pdf') {
        return <TbFileTypePdf size={32} />;
    }

    // Archive files
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
        return <TbFileTypeZip size={32} />;
    }

    // Spreadsheet files
    if (['xlsx', 'xls', 'csv'].includes(extension)) {
        return <TbFileTypeXls size={32} />;
    }

    // Text files
    if (['txt', 'md', 'rtf'].includes(extension)) {
        return <TbFileTypeTxt size={32} />;
    }

    // Code files
    if (['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'scss', 'sass', 'less', 'json', 'xml', 'py', 'java', 'cpp', 'c', 'php', 'rb', 'go', 'rs'].includes(extension)) {
        return <IoCodeSlashOutline size={32} />;
    }

    // Document files
    if (['doc', 'docx', 'odt'].includes(extension)) {
        return <IoDocumentTextOutline size={32} />;
    }

    // Default file icon
    return <IoFileTrayOutline size={32} />;
};

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

const URL_REGEX = /(https?:\/\/[^\s]+|(?:www\.)?[a-z0-9.-]+\.[a-z]{2,}(?:\/[^\s]*)?)/gi;

// Valid TLDs for link preview detection
const VALID_TLDS = [
    // Reserved/Testing TLDs
    '.test', '.example', '.invalid', '.localhost',
    // Development
    '.dev', '.local', '.internal',
    // Generic TLDs (gTLDs)
    '.com', '.net', '.org', '.edu', '.gov', '.mil', '.int', '.info', '.biz', '.name', '.pro', '.coop', '.aero', '.museum',
    // Popular new gTLDs
    '.app', '.io', '.ai', '.tech', '.online', '.site', '.website', '.store', '.shop', '.blog', '.news', '.media', '.agency',
    '.company', '.digital', '.email', '.cloud', '.network', '.systems', '.technology', '.software', '.computer', '.codes',
    '.design', '.graphics', '.photo', '.photography', '.video', '.audio', '.music', '.film', '.studio', '.art', '.gallery',
    '.zone', '.space', '.world', '.earth', '.live', '.life', '.community', '.social', '.chat', '.link', '.click', '.wiki',
    '.academy', '.university', '.school', '.education', '.institute', '.college', '.work', '.career', '.jobs', '.services',
    '.solutions', '.support', '.help', '.guide', '.tips', '.tools', '.today', '.now', '.here', '.pub', '.cafe', '.bar',
    '.restaurant', '.pizza', '.food', '.recipes', '.cooking', '.kitchen', '.menu', '.fitness', '.health', '.medical', '.care',
    '.doctor', '.dental', '.surgery', '.clinic', '.hospital', '.money', '.finance', '.financial', '.bank', '.credit', '.loan',
    '.tax', '.accountant', '.trading', '.insurance', '.fund', '.ventures', '.capital', '.estate', '.homes', '.house', '.property',
    '.realty', '.land', '.casa', '.travel', '.flights', '.hotels', '.vacations', '.tours', '.cruise', '.auto', '.car', '.cars',
    '.taxi', '.bike', '.motorcycles', '.game', '.games', '.poker', '.casino', '.bet', '.tennis', '.golf', '.football', '.soccer',
    '.hockey', '.baseball', '.basketball', '.racing', '.team', '.coach', '.fans', '.fashion', '.clothing', '.shoes', '.jewelry',
    '.watch', '.boutique', '.style', '.beauty', '.makeup', '.hair', '.skin', '.tattoo', '.ink', '.gifts', '.flowers', '.toys',
    '.baby', '.kids', '.family', '.wedding', '.events', '.party', '.christmas', '.holiday',
    // Country Code TLDs (ccTLDs)
    '.us', '.uk', '.ca', '.au', '.de', '.fr', '.it', '.es', '.nl', '.be', '.ch', '.at', '.se', '.no', '.dk', '.fi', '.pl',
    '.cz', '.ru', '.ua', '.jp', '.cn', '.kr', '.in', '.sg', '.hk', '.tw', '.th', '.my', '.id', '.ph', '.vn', '.nz', '.za',
    '.br', '.mx', '.ar', '.cl', '.co', '.pe', '.ve', '.tr', '.il', '.ae', '.sa', '.eg', '.ng', '.ke', '.gh', '.ma', '.tn',
    // Special use
    '.tv', '.me', '.cc', '.ws', '.to', '.fm', '.am', '.ly', '.gl', '.im', '.ac', '.sh', '.tm', '.vc', '.ag', '.bz', '.mn',
    '.nu', '.sc', '.tk', '.ga', '.cf', '.gq', '.ml'
];

function hasValidTld(url) {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.toLowerCase();
        return VALID_TLDS.some(tld => hostname.endsWith(tld));
    } catch {
        return false;
    }
}

function normalizeUrl(raw) {
    if (!raw) return null;
    let u = raw.trim();
    // trailing punctuation temizliği
    u = u.replace(/[),.;!?]+$/, "");
    // protokol yoksa https ekle
    if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
    try {
        // geçerli URL mi?
        const url = new URL(u);
        // Check if TLD is valid
        if (!hasValidTld(u)) return null;
        return url.toString();
    } catch {
        return null;
    }
}

function extractUrls(text) {
    if (!text || typeof text !== "string") return [];
    // Remove email addresses before extracting URLs to prevent email domains from being treated as links
    const textWithoutEmails = text.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '');
    const found = textWithoutEmails.match(URL_REGEX) || [];
    const normalized = found
        .map(normalizeUrl)
        .filter(Boolean);
    return Array.from(new Set(normalized));
}

function getHostName(href) {
    if (!href) return "";
    try {
        const urlObj = new URL(href);
        return urlObj.host.replace(/^www\./i, "") || urlObj.host || href;
    } catch {
        return href;
    }
}

const LinkPreviewCard = ({ preview, href, getTranslation }) => {
    const urlObj = (() => { try { return new URL(href); } catch { return null; } })();
    const host = getHostName(href);
    const normalizedTitle = preview?.title?.trim() || href;
    const displayImage = preview?.image || previewPlaceholder;
    const isPlaceholder = !preview?.image;

    const origin = urlObj ? `${urlObj.protocol}//${urlObj.host}` : null;
    const primaryFavicon = origin ? `${origin}/favicon.ico` : "";
    const fallbackFavicon = urlObj ? `https://www.google.com/s2/favicons?domain=${urlObj.host}&sz=64` : "";
    const hasDescription = !!(preview?.description && preview.description.trim());
    const hasTitle = !!preview?.title?.trim();
    const defaultDescription = getTranslation ? getTranslation('clickToLearnMore') : 'Click to learn more';

    // Show title overlay when: has title AND description BUT no image
    // Show host overlay when: no image AND not showing title overlay
    const shouldShowTitleOverlay = hasTitle && hasDescription && isPlaceholder;
    const shouldShowHostOverlay = isPlaceholder && !shouldShowTitleOverlay;

    return (
        <a
            className="link-preview-card wa"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={normalizedTitle}
        >
            <div className={`wa-thumb ${isPlaceholder ? 'wa-thumb--placeholder' : ''}`}>
                <img
                    className="wa-thumb__img"
                    src={displayImage}
                    alt={normalizedTitle}
                    loading="lazy"
                    onError={(e) => {
                        if (e.currentTarget.src !== previewPlaceholder) {
                            e.currentTarget.src = previewPlaceholder;
                        }
                    }}
                />
                {shouldShowHostOverlay && (
                    <div className={`wa-thumb__placeholder-label ${!isPlaceholder ? 'wa-thumb__placeholder-label--on-image' : ''}`}>
                        {host}
                    </div>
                )}
                {shouldShowTitleOverlay && (
                    <div className="wa-thumb__placeholder-label wa-thumb__placeholder-label--on-image wa-thumb__placeholder-label--title">
                        <span>{preview.title}</span>
                    </div>
                )}
            </div>

            <div className="wa-meta">
                <div className="wa-title" title={normalizedTitle}>
                    {normalizedTitle}
                </div>

                <div
                    className={`wa-desc ${hasDescription ? '' : ''}`}
                >
                    {hasDescription ? preview.description : defaultDescription}
                </div>

                <div className="wa-host">
                    <img
                        className="wa-favicon"
                        src={primaryFavicon || fallbackFavicon}
                        onError={(e) => { if (e.currentTarget.src !== fallbackFavicon) e.currentTarget.src = fallbackFavicon; }}
                        alt=""
                    />
                    <span>{host}</span>
                </div>
            </div>
        </a>
    );
};

const LinkPreviewSkeleton = () => {
    return (
        <div className="link-preview-card wa is-loading" aria-hidden="true">
            <div className="wa-thumb">
                <div className="wa-thumb__skeleton skeleton-shimmer" />
            </div>
            <div className="wa-meta">
                <div className="wa-title skeleton-placeholder skeleton-shimmer">{'\u00A0'}</div>
                <div className="wa-desc skeleton-placeholder" aria-hidden="true">
                    <span className="skeleton-placeholder__line skeleton-shimmer">{'\u00A0'}</span>
                    <span className="skeleton-placeholder__line skeleton-shimmer">{'\u00A0'}</span>
                </div>
                <div className="wa-host skeleton-host">
                    <div className="wa-favicon skeleton-circle skeleton-shimmer" />
                    <span className="skeleton-placeholder skeleton-placeholder--host skeleton-shimmer">{'\u00A0'}</span>
                </div>
            </div>
        </div>
    );
};

const SocialHiveChat = ({ activeRoom, setActiveRoom, setActiveChannel, selectedChannel }) => {
    const { getTranslation, language } = useLanguage();
    const { userId, userInformation, setCreateProjectLoading, myChannels } = useContext(GeneralContext);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const socketRef = useRef();
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const [isAdmin, setIsAdmin] = useState("0");
    const [isDeleteMessageOpen, setIsDeleteMessageOpen] = useState(false);
    const [deleteMessageID, setDeleteMessageID] = useState("");
    const [isDeleteDMMessageOpen, setIsDeleteDMMessageOpen] = useState(false);
    const [deleteDMMessageID, setDeleteDMMessageID] = useState("");

    const messagesContainerRef = useRef(null);
    const skipScrollRef = useRef(false);
    const messageRefs = useRef({});
    const prevScrollHeightRef = useRef(0);
    const userWasAtBottomRef = useRef(true);

    const [attachments, setAttachments] = useState([]);
    const fileInputRef = useRef();

    const [uploadProgress, setUploadProgress] = useState(0);
    const totalBytesRef = useRef(0);
    const loadedBytesRef = useRef(0);
    const initialLoadingPromiseRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [typingUser, setTypingUser] = useState(null);
    const [starredMessages, setStarredMessages] = useState({});
    // Local reactions and mock tagging state helpers
    const [reactionsByMessage, setReactionsByMessage] = useState({}); // { [msgId]: { [emoji]: { count, reacted, emotionID } } }
    const PICKER_EMOJIS = ['👍', '❤️', '😂', '🎉', '😮', '😢', '😎', '🤔', '😇', '🔥', '✅', '❌', '🙌', '👏', '🙏', '💯', '🤯', '🥳'];
    const [emojiDialogFor, setEmojiDialogFor] = useState(null);
    const [hoveredReadReceipt, setHoveredReadReceipt] = useState(null);

    // Helper function to parse emotions array into reactionsByMessage format
    const parseEmotionsToReactions = (emotions, messageId) => {
        if (!emotions || !Array.isArray(emotions) || emotions.length === 0) {
            return null;
        }

        const messageReactions = {};
        emotions.forEach((emotion, index) => {
            if (emotion.content && emotion.count !== undefined) {
                // Check if current user reacted by checking details array
                // Also extract emotion_id from the user's detail object
                let userReacted = false;
                let emotionID = null;
                
                if (Array.isArray(emotion.details)) {
                    const userDetail = emotion.details.find(detail => detail.user_id === userId);
                    if (userDetail) {
                        userReacted = true;
                        // emotion_id is in the detail object, not the emotion object
                        emotionID = userDetail.emotion_id || userDetail.emotionID || userDetail.id;
                    }
                }
                
                messageReactions[emotion.content] = {
                    count: emotion.count || 0,
                    reacted: userReacted,
                    emotionID: emotionID
                };
            }
        });

        return Object.keys(messageReactions).length > 0 ? { [messageId]: messageReactions } : null;
    };

    const toggleReactionGlobal = async (messageId, emoji) => {
        const token = localStorage.getItem("token");
        const isDM = selectedChannel.channelID === "0";
        const channelID = selectedChannel.channelID;
        const roomID = activeRoom.roomID;
        const currentReaction = reactionsByMessage[messageId]?.[emoji];
        const isRemoving = currentReaction?.reacted;

        // Optimistically update local state
        const previousState = currentReaction || { count: 0, reacted: false, emotionID: null };
        
        setReactionsByMessage(prev => {
            const messageReacts = prev[messageId] || {};
            const current = messageReacts[emoji] || { count: 0, reacted: false, emotionID: null };
            
            if (isRemoving) {
                // Remove reaction
                const next = { 
                    count: Math.max(0, current.count - 1), 
                    reacted: false,
                    emotionID: null
                };
                // If count becomes 0, remove the emoji entry
                if (next.count === 0) {
                    const { [emoji]: removed, ...rest } = messageReacts;
                    return { ...prev, [messageId]: rest };
                }
                return { ...prev, [messageId]: { ...messageReacts, [emoji]: next } };
            } else {
                // Add reaction
                const next = { 
                    count: current.count + 1, 
                    reacted: true,
                    emotionID: current.emotionID || null
                };
                
                // Scroll to bottom of chat after DOM updates to show the reaction (with delay for smoother animation)
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setTimeout(() => {
                            const container = messagesContainerRef.current;
                            if (container) {
                                container.scrollTo({
                                    top: container.scrollHeight,
                                    behavior: 'smooth'
                                });
                            }
                        }, 150);
                    });
                });
                
                return { ...prev, [messageId]: { ...messageReacts, [emoji]: next } };
            }
        });

        // Call API to persist reaction change
        try {
            if (isRemoving) {
                // Remove reaction
                const emotionID = currentReaction?.emotionID;
                if (!emotionID) {
                    console.error("Cannot remove reaction: emotionID not found");
                    // Revert optimistic update
                    setReactionsByMessage(prev => {
                        const messageReacts = prev[messageId] || {};
                        return { ...prev, [messageId]: { ...messageReacts, [emoji]: previousState } };
                    });
                    return;
                }

                if (isDM) {
                    await Services.SocialHiveRemoveEmotionFromDMMessage(
                        token,
                        messageId,
                        roomID,
                        emotionID
                    );
                } else {
                    await Services.SocialHiveRemoveEmotionFromChannelMessage(
                        token,
                        messageId,
                        roomID,
                        channelID,
                        emotionID
                    );
                }
            } else {
                // Add reaction
                if (isDM) {
                    await Services.SocialHiveAddEmotionToDMMessage(
                        token,
                        messageId,
                        roomID,
                        emoji
                    );
                } else {
                    await Services.SocialHiveAddEmotionToChannelMessage(
                        token,
                        messageId,
                        roomID,
                        channelID,
                        emoji
                    );
                }
                
                // Refetch message to get the emotionID from the server response
                try {
                    const res = isDM
                        ? await Services.SocialHiveGetDMMessageByID(token, messageId)
                        : await Services.SocialHiveGetChannelMessageByID(token, messageId, roomID);
                    if (res?.data?.status && res?.data?.result?.emotions) {
                        // Parse the updated emotions to get the emotionID
                        const parsedReactions = parseEmotionsToReactions(res.data.result.emotions, messageId);
                        if (parsedReactions && parsedReactions[messageId]?.[emoji]?.emotionID) {
                            // Update the emotionID in the state
                            setReactionsByMessage(prev => {
                                const messageReacts = prev[messageId] || {};
                                const current = messageReacts[emoji];
                                if (current) {
                                    return {
                                        ...prev,
                                        [messageId]: {
                                            ...messageReacts,
                                            [emoji]: {
                                                ...current,
                                                emotionID: parsedReactions[messageId][emoji].emotionID
                                            }
                                        }
                                    };
                                }
                                return prev;
                            });
                        }
                    }
                } catch (refetchError) {
                    console.error("Error refetching message after adding reaction:", refetchError);
                    // Don't revert on refetch error, the reaction was still added
                }
            }
        } catch (error) {
            console.error(`Error ${isRemoving ? 'removing' : 'adding'} reaction:`, error);
            // Revert optimistic update on error
            setReactionsByMessage(prev => {
                const messageReacts = prev[messageId] || {};
                return { ...prev, [messageId]: { ...messageReacts, [emoji]: previousState } };
            });
        }
    };
    const [linkPreviews, setLinkPreviews] = useState(() => ({})); // { [url]: { title, description, image, url } | { error:true } }
    const fetchingUrlsRef = useRef(new Set()); // aynı anda tekrar çağrı olmasın

    const typingTimeoutRef = useRef(null);
    const pendingNotificationTimersRef = useRef({});

    const formatBytes = (bytes) => {
        if (!bytes && bytes !== 0) return null;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        if (bytes === 0) return '0 B';
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        const value = bytes / Math.pow(1024, i);
        return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${sizes[i]}`;
    };

    const getAttachmentSize = (file) => {
        // Try common fields; may be missing for historical data
        return file.size || file.fileSize || file.file_size || file.sizeBytes || file.bytes || null;
    };

    // Auto-resize textarea function
    const autoResizeTextarea = () => {
        const textarea = textareaRef.current;
        const inputArea = textarea?.closest('.social-hive-chat__input-area');

        if (textarea && inputArea) {
            textarea.style.height = 'auto';
            const scrollHeight = textarea.scrollHeight;
            const maxHeight = 120; // Maximum height in pixels (about 5-6 lines)

            // Only resize if content exceeds single line height (approximately 24px)
            if (scrollHeight > 24) {
                textarea.style.height = Math.min(scrollHeight, maxHeight) + 'px';
                inputArea.classList.remove('single-line');
            } else {
                // Keep at single line height
                textarea.style.height = '24px';
                inputArea.classList.add('single-line');
            }
        }
    };

    // Reset textarea height after sending message
    const resetTextareaHeight = () => {
        const textarea = textareaRef.current;
        const inputArea = textarea?.closest('.social-hive-chat__input-area');

        if (textarea && inputArea) {
            textarea.style.height = 'auto';
            // Ensure single-line class is applied when reset
            inputArea.classList.add('single-line');
        }
    };

    const [modalData, setModalData] = useState({
        isOpen: false,
        fileUrl: '',
        fileName: '',
        mimeType: '',
        messageData: null,
        gallery: null,
        initialIndex: 0
    });

    const [dragDropVisible, setDragDropVisible] = useState(false);

    // Drag and drop event handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragDropVisible(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only hide if we're leaving the entire window
        if (!e.relatedTarget || e.relatedTarget === document.body) {
            setDragDropVisible(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragDropVisible(false);

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            // Process the dropped files
            let newTotal = attachments.reduce((sum, f) => sum + f.size, 0);
            const valid = [];
            for (const file of files) {
                if (attachments.length + valid.length >= MAX_FILES) break;
                if (newTotal + file.size > MAX_TOTAL_SIZE) break;
                valid.push(file);
                newTotal += file.size;
            }
            setAttachments(prev => [...prev, ...valid]);
        }
    };

    useEffect(() => {
        // aktif oda mesajlarını al
        const roomMsgs = messages.filter(m => m.roomID === activeRoom.roomID);
        // mesajlardaki tüm URL'leri topla
        const allUrls = new Set();
        roomMsgs.forEach(m => {
            const urls = extractUrls(m.text);
            urls.forEach(u => allUrls.add(u));
        });

        // henüz fetch etmediğimiz URL’leri sıraya koy
        const toFetch = Array.from(allUrls).filter(u => !linkPreviews[u] && !fetchingUrlsRef.current.has(u));
        if (toFetch.length === 0) return;

        const token = localStorage.getItem("token");

        // sırayla veya küçük batch’lerle çağırabiliriz; burada basit sıra
        (async () => {
            for (const url of toFetch) {
                try {
                    fetchingUrlsRef.current.add(url);
                    const res = await Services.GetLinkPreview(token, url);
                    if (res?.data?.status && res?.data?.result) {
                        // service "url" alanını da döndürüyor; normalize’ı koruyalım
                        setLinkPreviews(prev => ({
                            ...prev,
                            [url]: {
                                title: res.data.result.title || "",
                                description: res.data.result.description || "",
                                image: res.data.result.image || null,
                                url: normalizeUrl(res.data.result.url || url) || url
                            }
                        }));
                    } else {
                        setLinkPreviews(prev => ({ ...prev, [url]: { error: true } }));
                    }
                } catch (e) {
                    setLinkPreviews(prev => ({ ...prev, [url]: { error: true } }));
                } finally {
                    fetchingUrlsRef.current.delete(url);
                }
            }
        })();
    }, [messages, activeRoom.roomID]);

    const handleStarClick = async (messageID) => {
        const token = localStorage.getItem('token');
        const isCurrentlyStarred = starredMessages[messageID];

        try {
            let res;
            if (selectedChannel.channelID === "0") {
                if (isCurrentlyStarred) {
                    res = await Services.UnstarDMMessage(token, messageID, activeRoom.roomID);
                } else {
                    res = await Services.StarDMMessage(token, messageID, activeRoom.roomID);
                }
            }
            else {
                if (isCurrentlyStarred) {
                    res = await Services.UnstarChannelMessage(token, messageID, activeRoom.roomID, selectedChannel.channelID);
                } else {
                    res = await Services.StarChannelMessage(token, messageID, activeRoom.roomID, selectedChannel.channelID);
                }
            }

            if (res.data.status) {
                setStarredMessages((prev) => ({
                    ...prev,
                    [messageID]: !prev[messageID],
                }));
            } else {
                console.warn("❌ Yıldız işlemi başarısız:", res.data.message || res.data);
            }

        } catch (err) {
            console.error("🚨 Yıldız işlemi sırasında hata:", err);
        }
    };

    useEffect(() => {
        initialLoadingPromiseRef.current = {};
        initialLoadingPromiseRef.current.promise = new Promise((resolve) => {
            initialLoadingPromiseRef.current.resolve = resolve;
        });

        setInputValue("");
        resetTextareaHeight();
    }, [activeRoom.roomID]);

    // Initialize single-line class on component mount
    useEffect(() => {
        const textarea = textareaRef.current;
        const inputArea = textarea?.closest('.social-hive-chat__input-area');
        if (inputArea) {
            inputArea.classList.add('single-line');
        }
    }, []);

    const handleAddClick = () => {
        fileInputRef.current.click();
    };

    const handleFilesSelected = (e) => {
        const files = Array.from(e.target.files);
        let newTotal = attachments.reduce((sum, f) => sum + f.size, 0);
        const valid = [];
        for (const file of files) {
            if (attachments.length + valid.length >= MAX_FILES) break;
            if (newTotal + file.size > MAX_TOTAL_SIZE) break;
            valid.push(file);
            newTotal += file.size;
        }
        setAttachments(prev => [...prev, ...valid]);
        e.target.value = '';
    };

    // Remove one attachment
    const removeAttachment = (idx) => {
        setAttachments(prev => prev.filter((_, i) => i !== idx));
    };


    useEffect(() => {
        if (activeRoom.channelID) {
            if (myChannels.find(x => x.channelID === selectedChannel.channelID)) {
                setIsAdmin(myChannels.find(x => x.channelID === selectedChannel.channelID).isAdmin)
            }
        }
    }, []);

    const loadPreviousMessages = async () => {
        if (skipScrollRef.current) return;

        const container = messagesContainerRef.current;
        if (!container) return;

        prevScrollHeightRef.current = container.scrollHeight;
        if (initialLoadingPromiseRef.current?.promise) {
            await initialLoadingPromiseRef.current.promise;
        }

        const token = localStorage.getItem('token');
        const firstMsgID = messages.length > 0 ? messages[0].id : null;

        try {
            const res = activeRoom.channelID
                ? await Services.SocialHiveGetPreviousRoomMessages(token, activeRoom.channelID, activeRoom.roomID, firstMsgID)
                : await Services.SocialHiveGetPreviousDMRoomMessages(token, activeRoom.roomID, firstMsgID);

            if (res.data.status && res.data.result.length) {
                const fetched = res.data.result
                    .slice()
                    .reverse()
                    .map(item => ({
                        id: item.messageID,
                        senderId: activeRoom.channelID ? item.userID : item.sendBy,
                        type: (activeRoom.channelID ? item.userID : item.sendBy) === userId ? 'sent' : 'received',
                        text: normalizeContent(item.content),
                        status: 'received',
                        roomID: item.roomID,
                        isRead: item.isRead,
                        createdDateTime: parseInt(item.createdDateTime, 10),
                        userName: `${item.user.name} ${item.user.lastname}`,
                        userAvatar: item.user.userAvatar,
                        attachments: item.files,
                        isStarred: Array.isArray(item.starred) ? item.starred.some(s => s.userID === userId) : false,
                        isTagged: false
                    }));

                // ⭐ Yıldızlı mesajları state'e kaydet
                const newStarred = {};
                fetched.forEach(msg => {
                    if (msg.isStarred) {
                        newStarred[msg.id] = true;
                    }
                });
                setStarredMessages(prev => ({ ...prev, ...newStarred }));

                if (!activeRoom.channelID) {
                    fetched.forEach(msg => {
                        if (msg.isRead === false) {
                            sendReadReceipt(msg);
                        }
                    });
                }

                skipScrollRef.current = true;
                setMessages(prev => [...fetched, ...prev]);
            }
        } catch (err) {
            console.error('loadPreviousMessages error:', err);
        }
    };

    useLayoutEffect(() => {
        if (skipScrollRef.current) {
            const container = messagesContainerRef.current;
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - prevScrollHeightRef.current;
        }
    }, [messages]);

    const handleScroll = () => {
        if (skipScrollRef.current) return; // scroll programatikse çalışmasın
        const container = messagesContainerRef.current;
        userWasAtBottomRef.current = isUserAtBottom();
        if (container.scrollTop === 0) {
            loadPreviousMessages();
        }
    };

    const scrollToBottom = () => {
        userWasAtBottomRef.current = true;
        messagesEndRef.current?.scrollIntoView();
    };

    const sendReadReceipt = (message) => {

        if (!message || !message.senderId || message.senderId === userId) return;

        let msgID = message.id || message.messageID

        const token = localStorage.getItem("token");
        Services.SocialHiveSetDMRead(token, activeRoom.roomID, msgID)
            .then(res => {
                const payload = {
                    module: 'social-hive',
                    type: 'read-receipt',
                    roomID: message.roomID,
                    content: {
                        messageID: msgID,
                        isRead: "1",
                        readBy: userId
                    },
                    createdDateTime: parseInt(Date.now() / 1000, 10)
                };

                socketRef.current.emit('message', { room: activeRoom.roomID, message: payload });
            })
            .catch(console.error);
    };

    const fetchNewMessagesAfterReconnect = async () => {
        const token = localStorage.getItem("token");
        const lastMsgID = messages.length > 0 ? messages[messages.length - 1].id : null;

        try {
            const res = activeRoom.channelID
                ? await Services.SocialHiveGetRoomMessages(token, activeRoom.channelID, activeRoom.roomID, lastMsgID)
                : await Services.SocialHiveGetDMRoomMessages(token, activeRoom.roomID, lastMsgID);

            if (res.data.status && res.data.result.length) {
                const fetched = res.data.result
                    .slice()
                    .reverse()
                    .map(item => {
                        const isStarred = Array.isArray(item.starred)
                            ? item.starred.some(s => s.userID === userId)
                            : false;

                        return {
                            id: item.messageID,
                            senderId: activeRoom.channelID ? item.userID : item.sendBy,
                            type: (activeRoom.channelID ? item.userID : item.sendBy) === userId ? 'sent' : 'received',
                            text: normalizeContent(item.content),
                            status: 'received',
                            isRead: item.isRead,
                            roomID: item.roomID,
                            createdDateTime: parseInt(item.createdDateTime, 10),
                            userName: `${item.user.name} ${item.user.lastname}`,
                            userAvatar: item.user.userAvatar,
                            attachments: item.files,
                            isStarred, // ←⭐️ burası yeni
                            isTagged: false
                        };
                    });
                setMessages(prev => {
                    const existingIds = new Set(prev.map(m => m.id));
                    const newMsgs = fetched.filter(m => !existingIds.has(m.id));
                    return [...prev, ...newMsgs];
                });

                // fetched mesajlardan yıldızlı olanları al
                const initialStarred = {};
                fetched.forEach(msg => {
                    if (msg.isStarred) {
                        initialStarred[msg.id] = true;
                    }
                });
                setStarredMessages(initialStarred);

                if (!activeRoom.channelID) {
                    fetched.forEach(msg => {
                        if (msg.isRead === "0" || msg.isRead === false) {
                            sendReadReceipt(msg);
                        }
                    });
                }
            }
        } catch (error) {
            console.error("fetchNewMessagesAfterReconnect error:", error);
        }
    };

    function normalizeContent(content) {
        // 1) Eğer zaten bir obje/array ise direkt stringify et
        if ((typeof content === 'object' || Array.isArray(content)) && content !== null) {
            return JSON.stringify(content);
        }

        // 2) Eğer string ise, JSON.parse edilebiliyor mu diye dene
        if (typeof content === 'string') {
            try {
                const parsed = JSON.parse(content);
                // parse edildiyse tekrar stringify et
                return JSON.stringify(parsed);
            } catch (e) {
                // parse edilemedi — muhtemelen normal bir metin
                return content;
            }
        }

        // 3) Diğer durumlarda da toString() ile stringe çevir
        return String(content);
    }

    useEffect(() => {

        socketRef.current = io(SOCKET_URL, {
            reconnection: true,
            reconnectionAttempts: Infinity,
            transports: ['websocket'],
        });

        const socket = socketRef.current;

        const setupListeners = () => {
            socket.on('message', handleIncomingMessage);
        };


        socket.on('connect', () => {
            socketRef.current.emit('joinRoom', activeRoom.roomID);
            setIsConnected(true);
            setupListeners();
            fetchNewMessagesAfterReconnect();
        });

        socket.on('disconnect', () => {
            setIsConnected(false);
        });

        setupListeners();

        return () => {
            socket.off('message', handleIncomingMessage);
            socketRef.current.disconnect();
            setMessages([])
        };
    }, [userId, activeRoom.roomID]);

    const clearTypingIndicator = () => {
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
        }
        setTypingUser(null);
    };

    const handleIncomingMessage = (msg) => {
        // Log entire message object to inspect reaction data structure
        console.log("Incoming message object:", msg);

        if (
            !activeRoom.channelID &&
            msg.type !== 'typing' &&
            msg.content?.userID &&
            msg.content.userID !== userId
        ) {
            clearTypingIndicator();
        }

        if (msg.type === 'read-receipt') {
            const { messageID } = msg.content;
            const timer = pendingNotificationTimersRef.current[messageID];
            if (timer) {
                clearTimeout(timer);
                delete pendingNotificationTimersRef.current[messageID];
            }

            setMessages(prev =>
                prev.map(m =>
                    (m.id === messageID || m.messageID === messageID)
                        ? { ...m, status: 'read', isRead: '1' }
                        : m
                )
            );
            return;
        }

        if (msg.type === 'delete-message') {
            const { messageID } = msg;
            setMessages(prev => prev.filter(msg => msg.id !== messageID));
            return;
        }

        if (!activeRoom.channelID) {
            sendReadReceipt(msg);
        }

        if (msg.content.userID === userId) return;

        if (msg.type === 'typing') {
            if (!activeRoom.channelID && msg.content.userID !== userId) {
                setTypingUser({ userAvatar: msg.content.userAvatar, username: msg.content.username });
                if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                }

                typingTimeoutRef.current = setTimeout(() => {
                    setTypingUser(null);
                    typingTimeoutRef.current = null;
                }, 2000);
            }
        }

        skipScrollRef.current = true;

        if (msg.isWithAttachment) {
            const token = localStorage.getItem("token");
            const fetchFn = activeRoom.channelID
                ? Services.SocialHiveGetChannelMessageByID
                : Services.SocialHiveGetDMMessageByID;

            fetchFn(token, msg.messageID, activeRoom.roomID)
                .then((res) => {
                    if (res.data.status) {
                        const newMsg = {
                            id: msg.messageID || Date.now(),
                            type: 'received',
                            senderId: msg.content.userID,
                            text: msg.content.content,
                            status: 'received',
                            roomID: msg.roomID,
                            isRead: msg.isRead,
                            userName: msg.content.username,
                            createdDateTime: msg.createdDateTime,
                            userAvatar: msg.content.userAvatar,
                            attachments: res.data.result.files,
                            isTagged: false
                        };
                        setMessages((prev) => [...prev, newMsg]);

                        // Parse emotions from the fetched message
                        if (res.data.result.emotions) {
                            const parsedReactions = parseEmotionsToReactions(res.data.result.emotions, newMsg.id);
                            if (parsedReactions) {
                                setReactionsByMessage(prev => ({ ...prev, ...parsedReactions }));
                            }
                        }
                    }
                });
        } else {
            if (msg.type !== 'typing') {
                const newMsg = {
                    id: msg.messageID || Date.now(),
                    type: 'received',
                    senderId: msg.content.userID,
                    text: msg.content.content,
                    status: 'received',
                    roomID: msg.roomID,
                    isRead: msg.isRead,
                    userName: msg.content.username,
                    createdDateTime: msg.createdDateTime,
                    userAvatar: msg.content.userAvatar,
                    attachments: [],
                    isTagged: false
                };
                setMessages((prev) => [...prev, newMsg]);

                // Parse emotions from incoming message if present
                if (msg.emotions) {
                    const parsedReactions = parseEmotionsToReactions(msg.emotions, newMsg.id);
                    if (parsedReactions) {
                        setReactionsByMessage(prev => ({ ...prev, ...parsedReactions }));
                    }
                }
            }
        }
    }

    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            Object.values(pendingNotificationTimersRef.current || {}).forEach((t) => clearTimeout(t));
            pendingNotificationTimersRef.current = {};
        };
    }, []);

    useEffect(() => {
        const token = localStorage.getItem("token");
        setCreateProjectLoading(true);

        const fetchMessages = async () => {
            try {
                const res = activeRoom.channelID
                    ? await Services.SocialHiveGetRoomMessages(token, activeRoom.channelID, activeRoom.roomID)
                    : await Services.SocialHiveGetDMRoomMessages(token, activeRoom.roomID);

                setCreateProjectLoading(false);

                if (!res.data.status) return;

                const fetched = res.data.result
                    .slice()
                    .reverse()
                    .map(item => ({
                        id: item.messageID,
                        senderId: activeRoom.channelID ? item.userID : item.sendBy,
                        type: (activeRoom.channelID ? item.userID : item.sendBy) === userId ? 'sent' : 'received',
                        text: normalizeContent(item.content),
                        status: 'received',
                        isRead: item.isRead,
                        roomID: item.roomID,
                        createdDateTime: parseInt(item.createdDateTime, 10),
                        userName: `${item.user.name} ${item.user.lastname}`,
                        userAvatar: item.user.userAvatar,
                        attachments: item.files,
                        isStarred: Array.isArray(item.starred) ? item.starred.some(s => s.userID === userId) : false
                    }));

                setMessages(prev => {
                    const existingIds = new Set(prev.map(m => m.id));
                    const newMsgs = fetched.filter(m => !existingIds.has(m.id));
                    return [...newMsgs, ...prev];
                });

                // ⭐ Yıldızlı mesajları işaretle
                const initialStarred = {};
                fetched.forEach(msg => {
                    if (msg.isStarred) {
                        initialStarred[msg.id] = true;
                    }
                });
                setStarredMessages(initialStarred);

                // Parse emotions from fetched messages and populate reactionsByMessage
                const initialReactions = {};
                res.data.result.forEach(item => {
                    if (item.emotions && Array.isArray(item.emotions) && item.emotions.length > 0) {
                        const messageId = item.messageID;
                        const parsedReactions = parseEmotionsToReactions(item.emotions, messageId);
                        if (parsedReactions) {
                            Object.assign(initialReactions, parsedReactions);
                        }
                    }
                });
                if (Object.keys(initialReactions).length > 0) {
                    setReactionsByMessage(prev => ({ ...prev, ...initialReactions }));
                }

                if (!activeRoom.channelID) {
                    fetched.forEach(msg => {
                        if (msg.isRead === false) {
                            sendReadReceipt(msg);
                        }
                    });
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (initialLoadingPromiseRef.current && initialLoadingPromiseRef.current.resolve) {
                    initialLoadingPromiseRef.current.resolve();
                }
            }
        };

        fetchMessages();
    }, [activeRoom.channelID, activeRoom.roomID, userId]);

    useEffect(() => {
        if (skipScrollRef.current) {
            skipScrollRef.current = false;
            return;
        }
        scrollToBottom();
    }, [messages]);


    function generateAlphanumericCode(length = 12) {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return result;
    }


    const sendMessageToService = (msgID, content, files = []) => {
        const token = localStorage.getItem("token");

        const uploadFiles = (createFn) => {
            const promises = files.map(f => {
                const base64Data = f.file.split(",")[1];
                return createFn(
                    token,
                    base64Data,
                    f.extension,
                    f.name,
                    activeRoom.roomID,
                    msgID
                )
                    .then(fileRes => {
                        return fileRes.data.status === true;
                    })
                    .catch(err => {
                        console.error("[Upload Error]", {
                            name: f.name,
                            error: err
                        });
                        return false;
                    });
            });

            return Promise.all(promises)
                .then(results => results.every(r => r));
        };

        const handleSend = (sendFn, createFn) => {
            return sendFn(token, activeRoom.channelID ?? activeRoom.roomID, activeRoom.roomID, content.replaceAll('"', "''"), msgID)
                .then(res => {
                    if (!res.data.status) return false;
                    if (files.length === 0) return true;
                    return uploadFiles(createFn);
                })
                .catch(() => false);
        };

        if (activeRoom.channelID) {
            return handleSend(
                Services.SocialHiveSendMessage,
                Services.SocialHiveCreateRoomFiles
            );
        } else {
            return handleSend(
                // DM mesajda parametre sırası biraz farklı olabilir:
                (t, channelOrRoomId, roomId, cnt, id) => Services.SocialHiveDMSendMessage(t, roomId, cnt, id),
                Services.SocialHiveCreateDMFiles
            );
        }
    };

    const sendInfoToSocket = () => {
        const payload = {
            module: 'social-hive',
            type: "new-message",
            roomID: activeRoom.roomID,
            content: {
                userID: userId,
                username: "",
                content: "user joined your channel"
            },
            createdDateTime: new Date()
        };

        let idToSend = ""

        if (activeRoom.receiverID === userId) {
            idToSend = activeRoom.ownerDetails.id
        }
        else {
            idToSend = activeRoom.receiverDetails.id
        }

        socketRef.current.emit('message', { room: "DM" + idToSend, message: payload });

    };

    const sendMessage = () => {
        if (!inputValue.trim() && attachments.length === 0) return;
        setUploadProgress(0);
        loadedBytesRef.current = 0;
        totalBytesRef.current = attachments.reduce((sum, f) => sum + f.size, 0);

        const messageID = generateAlphanumericCode();
        const rawContent = inputValue.trim();

        Promise.all(
            attachments.map(file =>
                new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    let prevLoaded = 0;

                    reader.onprogress = evt => {
                        if (!evt.lengthComputable) return;
                        const delta = evt.loaded - prevLoaded;
                        prevLoaded = evt.loaded;
                        loadedBytesRef.current += delta;
                        const percent = Math.min(
                            100,
                            Math.round((loadedBytesRef.current / totalBytesRef.current) * 100)
                        );
                        setUploadProgress(percent);
                    };

                    reader.onload = () =>
                        resolve({
                            file: reader.result,
                            extension: "." + file.name.split(".").pop(),
                            name: file.name,
                            mimeType: file.type
                        });
                    reader.onerror = err => {
                        console.error("[Attachment] FileReader hatası:", file.name, err);
                        reject(err);
                    };
                    reader.readAsDataURL(file);
                })
            )
        )
            .then(formattedFiles => {
                const payload = {
                    module: "social-hive",
                    type: "send-message",
                    roomID: activeRoom.roomID,
                    messageID,
                    senderId: userId,
                    content: {
                        userID: userId,
                        username: `${userInformation.name} ${userInformation.lastname}`,
                        userAvatar: userInformation.userAvatar,
                        content: rawContent
                    },
                    createdDateTime: Math.floor(Date.now() / 1000),
                    isWithAttachment: formattedFiles.length > 0
                };

                sendMessageToService(messageID, rawContent, formattedFiles)
                    .then(res => {
                        if (res) {
                            socketRef.current.emit("message", {
                                room: activeRoom.roomID,
                                message: payload
                            });

                            if (!activeRoom.channelID) {
                                sendInfoToSocket();
                            }

                            const newMsg = {
                                id: messageID,
                                type: "sent",
                                senderId: userId,
                                text: rawContent,
                                status: "sent",
                                roomID: activeRoom.roomID,
                                isRead: "0",
                                userName: `${userInformation.name} ${userInformation.lastname}`,
                                createdDateTime: payload.createdDateTime,
                                userAvatar: userInformation.userAvatar,
                                attachments: formattedFiles,
                                isTagged: false
                            };
                            setMessages(prev => [...prev, newMsg]);
                            setInputValue("");
                            setAttachments([]);
                            resetTextareaHeight();
                            setTimeout(() => setUploadProgress(0), 500);

                            // 🔔 SADECE DM İÇİN: 2 sn içinde okundu gelmezse push notification
                            if (!activeRoom.channelID) {
                                // Karşı tarafı bul
                                const isCurrentUserReceiver = activeRoom.receiverID === userId;
                                const targetUserDetails = isCurrentUserReceiver
                                    ? activeRoom.ownerDetails
                                    : activeRoom.receiverDetails;

                                const recipientId = targetUserDetails?.id;
                                const expoTokens = Array.isArray(targetUserDetails?.expoTokens)
                                    ? targetUserDetails.expoTokens
                                    : [];

                                const senderName = `${userInformation.name} ${userInformation.lastname}`;
                                const messageContent = rawContent;

                                if (recipientId && expoTokens.length > 0) {
                                    const token = localStorage.getItem("token");

                                    const timerId = setTimeout(() => {
                                        // Eğer bu süre içinde read-receipt gelmişse timer zaten clear edildiği için burası çalışmaz.
                                        expoTokens.forEach((expoToken) => {
                                            Services.SendDMNotification(
                                                token,
                                                activeRoom.roomID,
                                                recipientId,
                                                senderName,
                                                messageContent,
                                                messageID,
                                                userId,
                                                expoToken
                                            ).catch((err) => {
                                                console.error("SendDMNotification error:", err);
                                            });
                                        });

                                        delete pendingNotificationTimersRef.current[messageID];
                                    }, 2000);

                                    pendingNotificationTimersRef.current[messageID] = timerId;
                                }
                            }

                        } else {
                            console.error("Mesaj servis kaydı başarısız:", res?.data);
                        }
                    })
                    .catch(err => {
                        console.error("Servis çağrısında hata:", err);
                    });
            })
            .catch(err => console.error("Dosya okuma hatası:", err));
    };

    function formatTimestamp(timestamp) {
        const date = new Date(timestamp * 1000);

        const pad = (num) => num.toString().padStart(2, '0');

        const day = pad(date.getDate());
        const month = pad(date.getMonth() + 1);
        const year = date.getFullYear();
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());

        return `${day}.${month}.${year}, ${hours}:${minutes}`;
    }



    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    function getFirstAndLastInitials(sentence) {
        const words = sentence
            .trim()
            .split(/\s+/)
            .filter(w => w.length > 0);
        if (words.length === 0) return '';
        const firstInitial = words[0][0];
        const lastInitial = words[words.length - 1][0];
        return (firstInitial + lastInitial).toUpperCase();
    }

    function getAvatarColor(userName) {
        if (!userName) return 'var(--avatar-color-1)';
        const initials = getFirstAndLastInitials(userName);
        if (!initials) return 'var(--avatar-color-1)';
        // Use a simple hash to consistently map to one of 4 colors
        const hash = initials.charCodeAt(0) + (initials.length > 1 ? initials.charCodeAt(1) : 0);
        const colorIndex = (hash % 4) + 1;
        return `var(--avatar-color-${colorIndex})`;
    }

    const handleAvatarDoubleClick = async (msg) => {
        // Only proceed if in a channel/group (not in DM) and not clicking own avatar
        if (!activeRoom.channelID || !msg.senderId || msg.senderId === userId) {
            return;
        }
        const targetUserId = msg.senderId;

        try {
            const token = localStorage.getItem("token");
            // Get current DM list to check if a conversation already exists with this user
            const resList = await Services.SocialHiveGetDMRoomList(token);
            let dmRoom = null;
            if (resList.data.status) {
                const dmList = resList.data.result;
                dmRoom = dmList.find(dm =>
                    dm.ownerID === targetUserId || dm.receiverID === targetUserId
                );
            }
            // If not found, create a new DM room
            if (!dmRoom) {
                const resCreate = await Services.SocialHiveCreateDM(token, targetUserId);
                if (resCreate.data.status) {
                    dmRoom = resCreate.data.result;
                }
            }
            if (dmRoom) {
                // Switch to DM channel and open the DM room
                setActiveChannel({ channelID: "0", title: "Direct Messages" });  // Switch sidebar to DM section
                setActiveRoom(dmRoom);  // Open the DM conversation with the target user
            }
        } catch (error) {
            console.error("Error opening DM:", error);
        }
    };

    const handleDeleteMessage = (messageID) => {

        const token = localStorage.getItem("token");
        Services.RemoveChannelMessage(token, activeRoom.channelID, activeRoom.roomID, messageID)
            .then(res => {
                const payload = {
                    module: 'social-hive',
                    type: 'delete-message',
                    roomID: activeRoom.roomID,
                    messageID: messageID
                };

                socketRef.current.emit('message', { room: activeRoom.roomID, message: payload });
            })
            .catch(console.error);
    };

    const handleDeleteDMMessage = (messageID) => {
        // Immediately remove message from UI
        setMessages(prev => prev.filter(msg => msg.id !== messageID));
        
        const token = localStorage.getItem("token");
        Services.RemoveDMMessage(token, messageID, activeRoom.roomID)
            .then(res => {
                if (res.data.status) {
                    const payload = {
                        module: 'social-hive',
                        type: 'delete-message',
                        roomID: activeRoom.roomID,
                        messageID: messageID
                    };

                    socketRef.current.emit('message', { room: activeRoom.roomID, message: payload });
                } else {
                    // If deletion failed, we might want to restore the message
                    // For now, we'll just log the error
                    console.error("Failed to delete message:", res.data);
                }
            })
            .catch(err => {
                console.error("Error deleting message:", err);
                // Optionally restore the message on error
            });
    };

    const sendTypingStatusToSocket = () => {
        if (!activeRoom.channelID && socketRef.current && activeRoom.roomID) {
            const payload = {
                module: 'social-hive',
                type: 'typing',
                roomID: activeRoom.roomID,
                content: {
                    userID: userId,
                    username: `${userInformation.name} ${userInformation.lastname}`,
                    userAvatar: userInformation.userAvatar
                },
                createdDateTime: Math.floor(Date.now() / 1000)
            };

            socketRef.current.emit('message', {
                room: activeRoom.roomID,
                message: payload
            });
        }
    };

    const isUserAtBottom = () => {
        const container = messagesContainerRef.current;
        if (!container) return false;

        const threshold = 100; // son 100px içindeyse en alttadır
        const position = container.scrollTop + container.clientHeight;
        return position >= container.scrollHeight - threshold;
    };

    useEffect(() => {
        if (typingUser && isUserAtBottom()) {
            scrollToBottom();
        }
    }, [typingUser]);

    useEffect(() => {
        if (userWasAtBottomRef.current) {
            scrollToBottom();
        }
    }, [linkPreviews]);

    function formatDateSeparator(timestamp) {
        const date = new Date(timestamp * 1000);
        const options = { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' };
        return date.toLocaleDateString(language, options);
    }

    return (
        <div
            className="social-hive-chat"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <div
                className="social-hive-chat__messages"
                ref={messagesContainerRef}
                onScroll={handleScroll}
            >
                {(() => {
                    let lastMessageDate = null;
                    const roomMessages = [...messages
                        .filter(x => x.roomID === activeRoom.roomID)
                        .reduce((acc, msg) => {
                            if (!acc.has(msg.id)) acc.set(msg.id, msg);
                            return acc;
                        }, new Map()).values()
                    ];
                    
                    // Find the most recent read message sent by current user
                    const mostRecentReadMessage = roomMessages
                        .filter(msg => msg.senderId === userId && msg.isRead === '1')
                        .sort((a, b) => b.createdDateTime - a.createdDateTime)[0];
                    const mostRecentReadMessageId = mostRecentReadMessage?.id;
                    
                    return roomMessages.map((msg, index, arr) => {
                        const currentMsgDate = new Date(msg.createdDateTime * 1000);
                        const currentDateString = currentMsgDate.toDateString();
                        let dateSeparator = null;

                        if (!lastMessageDate || currentDateString !== lastMessageDate) {
                            dateSeparator = (
                                <div key={`separator-${msg.id}`} className="date-separator">
                                    {formatDateSeparator(msg.createdDateTime)}
                                </div>
                            );
                            lastMessageDate = currentDateString;
                        }

                        const previousMsg = arr[index - 1];
                        const crossedDateBoundary = !!dateSeparator; // new day separator present
                        const sameSenderAsPrev = previousMsg && previousMsg.senderId === msg.senderId;

                        // Use same logic as timestamp display - show avatar if not same sender or not same minute
                        const currentTime = new Date(msg.createdDateTime * 1000);
                        const prevTime = previousMsg ? new Date(previousMsg.createdDateTime * 1000) : null;
                        const isSameMinute = prevTime &&
                            currentTime.getFullYear() === prevTime.getFullYear() &&
                            currentTime.getMonth() === prevTime.getMonth() &&
                            currentTime.getDate() === prevTime.getDate() &&
                            currentTime.getHours() === prevTime.getHours() &&
                            currentTime.getMinutes() === prevTime.getMinutes();

                        // Show avatar if:
                        // - date separator is shown (new day)
                        // - previous message is from different sender
                        // - or messages are not in the same minute (same logic as timestamp display)
                        const showAvatar = crossedDateBoundary || !sameSenderAsPrev || !isSameMinute;

                        // Determine if this is the last message in a stack (for read receipt display)
                        const nextMsg = arr[index + 1];
                        const nextTime = nextMsg ? new Date(nextMsg.createdDateTime * 1000) : null;
                        const isLastInStack = !nextMsg || 
                            nextMsg.senderId !== msg.senderId || 
                            (nextTime && (
                                currentTime.getFullYear() !== nextTime.getFullYear() ||
                                currentTime.getMonth() !== nextTime.getMonth() ||
                                currentTime.getDate() !== nextTime.getDate() ||
                                currentTime.getHours() !== nextTime.getHours() ||
                                currentTime.getMinutes() !== nextTime.getMinutes()
                            ));

                        const toggleReaction = (messageId, emoji) => {
                            setReactionsByMessage(prev => {
                                const messageReacts = prev[messageId] || {};
                                const current = messageReacts[emoji] || { count: 0, reacted: false };
                                const next = current.reacted
                                    ? { count: Math.max(0, current.count - 1), reacted: false }
                                    : { count: current.count + 1, reacted: true };
                                const updated = { ...messageReacts, [emoji]: next };
                                if (!updated[emoji].reacted && updated[emoji].count === 0) {
                                    delete updated[emoji];
                                }
                                return { ...prev, [messageId]: updated };
                            });
                        };

                        const toggleTagged = (messageId) => {
                            setMessages(prev => prev.map(m => m.id === messageId ? { ...m, isTagged: !m.isTagged } : m));
                        };

                        return (
                            <React.Fragment key={msg.id}>
                                {dateSeparator}
                                <div
                                    ref={(el) => { messageRefs.current[msg.id] = el; }}
                                    key={msg.id}
                                    className={`social-hive-chat__message social-hive-chat__message--${msg.type} ${msg.isTagged ? 'sh-message-tagged' : ''} ${showAvatar ? 'social-hive-chat__message--with-avatar' : ''}`}
                                >
                                    {/* Actions bubble - top right with favorite, add reaction, and tag */}
                                    <div className="sh-actions">
                                        {/* Favorite (star) */}
                                        <button
                                            className={`sh-fav ${starredMessages[msg.id] ? 'active' : ''}`}
                                            onClick={() => handleStarClick(msg.id)}
                                            title={starredMessages[msg.id] ? getTranslation('removeFavorite') : getTranslation('addFavorite')}
                                        >
                                            {starredMessages[msg.id] ? <TbStarFilled size={16} /> : <TbStar size={16} />}
                                        </button>
                                        {/* Delete button - only for DM messages sent by current user within 5 minutes */}
                                        {selectedChannel.channelID === "0" && 
                                         msg.senderId === userId && 
                                         (Date.now() / 1000) - msg.createdDateTime < 300 && (
                                            <button
                                                className="sh-fav sh-delete"
                                                onClick={() => {
                                                    setDeleteDMMessageID(msg.id);
                                                    setIsDeleteDMMessageOpen(true);
                                                }}
                                                title={getTranslation('delete') || 'Delete'}
                                            >
                                                <TbTrash size={16} />
                                            </button>
                                        )}
                                        {/* Add reaction (+) button */}
                                        <button
                                            className="sh-reaction-add"
                                            onClick={() => setEmojiDialogFor(msg.id)}
                                            title={getTranslation('addReaction')}
                                        >
                                            <TbPlus size={14} />
                                        </button>
                                        {/* Tag logic preserved for future service connection */}
                                        {/* toggleTagged(msg.id) - ready to connect to service */}
                                    </div>
                                    <div className={`social-hive-chat__message-row social-hive-chat__message-row--${msg.type} ${!showAvatar ? 'social-hive-chat__message-row--no-avatar' : ''}`}>
                                        {showAvatar && (
                                            msg.userAvatar ? (
                                                <img
                                                    src={msg.userAvatar}
                                                    className="social-hive-chat__avatar"
                                                    alt="avatar"
                                                    onDoubleClick={() => handleAvatarDoubleClick(msg)}
                                                />
                                            ) : (
                                                <div
                                                    onDoubleClick={() => handleAvatarDoubleClick(msg)}
                                                    className="social-hive-chat__avatar"
                                                    style={{ backgroundColor: getAvatarColor(msg.userName) }}
                                                >
                                                    {getFirstAndLastInitials(msg.userName)}
                                                </div>
                                            )
                                        )}
                                        <div style={{ flexDirection: 'column', display: 'flex', gap: '2px', width: '100%' }}>
                                            {(() => {
                                                const isGroupStart = !previousMsg || previousMsg.senderId !== msg.senderId || !isSameMinute;
                                                return isGroupStart ? (
                                                    <div className={`sh-msg-header ${msg.isTagged ? 'sh-msg-tagged' : ''}`}>
                                                        <span className="sh-msg-username">{msg.userName}</span>
                                                        <span className="sh-msg-timestamp">{formatTimestamp(msg.createdDateTime)}</span>
                                                    </div>
                                                ) : null;
                                            })()}

                                            {msg.attachments && msg.attachments.length > 0 && (
                                                <div className={`social-hive-chat__attachments ${msg.attachments.length > 2 ? 'grid' : ''}`}>
                                                    {msg.attachments.map((file, idx) => {

                                                        const isBase64Format = file.file !== undefined;

                                                        let uri = '';
                                                        let fileURL = '';
                                                        let mimeType = '';
                                                        let fileName = '';

                                                        if (isBase64Format) {
                                                            mimeType = file.mimeType;
                                                            fileName = file.name;

                                                            if (file.file.startsWith('data:')) {
                                                                const byteString = atob(file.file.split(',')[1]);
                                                                const mimeTypeFromData = file.file.match(/data:(.*);base64/)[1];
                                                                const byteNumbers = new Array(byteString.length);

                                                                for (let i = 0; i < byteString.length; i++) {
                                                                    byteNumbers[i] = byteString.charCodeAt(i);
                                                                }

                                                                const byteArray = new Uint8Array(byteNumbers);
                                                                const blob = new Blob([byteArray], { type: mimeTypeFromData });
                                                                uri = URL.createObjectURL(blob);
                                                                fileURL = uri; // Modalda açmak için uri kullanılır
                                                            } else {
                                                                uri = file.file;
                                                                fileURL = uri;
                                                            }
                                                        } else {
                                                            uri = file.thumbnail;
                                                            fileURL = file.fileURL || file.file_url;
                                                            mimeType = file.type;
                                                            fileName = file.file_original_name || file.fileOriginalName;
                                                        }

                                                        const isImage = mimeType.startsWith('image/');
                                                        const isVideo = mimeType.startsWith('video/');

                                                        return (
                                                            <div key={idx} className="social-hive-chat__attachment">
                                                                {isImage ? (
                                                                    <img
                                                                        src={uri}
                                                                        alt={fileName}
                                                                        style={{ cursor: 'var(--pointer)' }}
                                                                        onClick={() => {
                                                                            // Build gallery of only image attachments in this message
                                                                            const imageAttachments = (msg.attachments || []).map((att) => {
                                                                                const isBase64 = att.file !== undefined;
                                                                                let url = '';
                                                                                let type = '';
                                                                                let name = '';
                                                                                if (isBase64) {
                                                                                    type = att.mimeType;
                                                                                    name = att.name;
                                                                                    if (att.file.startsWith('data:')) {
                                                                                        try {
                                                                                            const byteString = atob(att.file.split(',')[1]);
                                                                                            const mimeTypeFromData = att.file.match(/data:(.*);base64/)[1];
                                                                                            const byteNumbers = new Array(byteString.length);
                                                                                            for (let i = 0; i < byteString.length; i++) byteNumbers[i] = byteString.charCodeAt(i);
                                                                                            const byteArray = new Uint8Array(byteNumbers);
                                                                                            const blob = new Blob([byteArray], { type: mimeTypeFromData });
                                                                                            url = URL.createObjectURL(blob);
                                                                                        } catch {
                                                                                            url = att.file;
                                                                                        }
                                                                                    } else {
                                                                                        url = att.file;
                                                                                    }
                                                                                } else {
                                                                                    url = att.fileURL || att.file_url;
                                                                                    type = att.type;
                                                                                    name = att.file_original_name || att.fileOriginalName;
                                                                                }
                                                                                return { url, name, type };
                                                                            }).filter(x => x.type && x.type.startsWith('image/'));

                                                                            const clickedIndex = imageAttachments.findIndex(x => x.url === fileURL);

                                                                            setModalData({
                                                                                isOpen: true,
                                                                                fileUrl: fileURL,
                                                                                fileName,
                                                                                mimeType,
                                                                                messageData: {
                                                                                    userName: msg.userName,
                                                                                    userAvatar: msg.userAvatar,
                                                                                    timestamp: msg.createdDateTime
                                                                                },
                                                                                gallery: imageAttachments.length > 1 ? imageAttachments : null,
                                                                                initialIndex: Math.max(0, clickedIndex)
                                                                            });
                                                                        }}
                                                                    />
                                                                ) : isVideo ? (
                                                                    <div
                                                                        className="social-hive-chat__video-preview"
                                                                        onClick={() =>
                                                                            setModalData({
                                                                                isOpen: true,
                                                                                fileUrl: fileURL,
                                                                                fileName,
                                                                                mimeType,
                                                                                messageData: {
                                                                                    userName: msg.userName,
                                                                                    userAvatar: msg.userAvatar,
                                                                                    timestamp: msg.createdDateTime
                                                                                }
                                                                            })
                                                                        }
                                                                        style={{
                                                                            position: 'relative',
                                                                            display: 'inline-block',
                                                                            cursor: 'var(--pointer)',
                                                                            maxWidth: '200px',
                                                                            borderRadius: '8px',
                                                                            overflow: 'hidden'
                                                                        }}
                                                                    >
                                                                        <video
                                                                            src={fileURL}
                                                                            poster={uri /* veya file.thumbnail */}
                                                                            style={{
                                                                                width: '100%',
                                                                                height: 'auto',
                                                                                pointerEvents: 'none'  // videonun native tıklamalarını engelle
                                                                            }}
                                                                        />

                                                                        <TbPlayerPlayFilled
                                                                            size={48}
                                                                            style={{
                                                                                position: 'absolute',
                                                                                top: '50%',
                                                                                left: '50%',
                                                                                transform: 'translate(-50%, -50%)',
                                                                                pointerEvents: 'none',
                                                                                color: 'rgba(255,255,255,0.8)'
                                                                            }}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className={`sh-file-attachment ${msg.type === 'sent' ? 'sent' : 'received'}`}
                                                                        onClick={() =>
                                                                            setModalData({
                                                                                isOpen: true,
                                                                                fileUrl: fileURL,
                                                                                fileName,
                                                                                mimeType,
                                                                                messageData: {
                                                                                    userName: msg.userName,
                                                                                    userAvatar: msg.userAvatar,
                                                                                    timestamp: msg.createdDateTime
                                                                                }
                                                                            })
                                                                        }
                                                                    >
                                                                        <div className="sh-file-attachment__icon">
                                                                            {getFileIcon(fileName, mimeType)}
                                                                        </div>
                                                                        <div className="sh-file-attachment__meta">
                                                                            <div className="sh-file-attachment__name" title={fileName}>{fileName}</div>
                                                                            {(() => {
                                                                                const sz = getAttachmentSize(file);
                                                                                return sz ? (
                                                                                    <div className="sh-file-attachment__size">{formatBytes(sz)}</div>
                                                                                ) : null;
                                                                            })()}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {msg.text &&
                                                <div className={`social-hive-chat__bubble ${msg.isTagged ? 'sh-bubble-tagged' : ''}`}>
                                                    {msg.text.split('\n').map((line, i) => (
                                                        <p key={i}>{linkifyText(line)}</p>
                                                    ))}
                                                    {/* --- ADD: Link Preview Cards (mesaj balonunun ALTINA) --- */}
                                                    {(() => {
                                                        const urls = extractUrls(msg.text);
                                                        if (!urls.length) return null;

                                                        return (
                                                            <div className="link-preview-list">
                                                                {urls.map((u) => {
                                                                    const pv = linkPreviews[u];

                                                                    if (!pv) {
                                                                        return <LinkPreviewSkeleton key={u} />;
                                                                    }

                                                                    const previewData = pv.error ? {
                                                                        title: "",
                                                                        description: "",
                                                                        image: null,
                                                                        url: u
                                                                    } : pv;

                                                                    const href = pv?.url || u;
                                                                    return <LinkPreviewCard key={u} preview={previewData} href={href} getTranslation={getTranslation} />;
                                                                })}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            }

                                            {/* Reactions toolbar */}
                                            {Object.entries(reactionsByMessage[msg.id] || {})
                                                .filter(([, stats]) => (stats?.count > 0) || !!stats?.reacted).length > 0 && (
                                                    <div className="sh-msg-toolbar">
                                                        <div className="sh-reactions">
                                                            {/* Existing reactions (persisted in local state) */}
                                                            {Object.entries(reactionsByMessage[msg.id] || {})
                                                                .filter(([, stats]) => (stats?.count > 0) || !!stats?.reacted)
                                                                .map(([emoji, stats]) => (
                                                                    <button
                                                                        key={emoji}
                                                                        className={`sh-reaction ${stats?.reacted ? 'active' : ''}`}
                                                                        onClick={() => toggleReactionGlobal(msg.id, emoji)}
                                                                        style={{ cursor: 'var(--pointer)' }}
                                                                    >
                                                                        <span>{emoji}</span>
                                                                        {stats?.count > 0 && <span className="sh-reaction-count">{stats.count}</span>}
                                                                    </button>
                                                                ))}
                                                        </div>
                                                    </div>
                                                )}
                                        </div>

                                    </div>
                                    {/* Read receipt avatar - show only on most recent read message */}
                                    {msg.senderId === userId && msg.id === mostRecentReadMessageId && (() => {
                                        // For DM messages, get opposite user
                                        if (!activeRoom.channelID) {
                                            const oppositeUser = activeRoom.receiverID === userId 
                                                ? activeRoom.ownerDetails 
                                                : activeRoom.receiverDetails;
                                            const oppositeUserName = oppositeUser ? `${oppositeUser.name || ''} ${oppositeUser.lastname || ''}`.trim() : '';
                                            
                                            // Log read receipt data
                                            console.log('Read Receipt Data:', {
                                                messageId: msg.id,
                                                messageText: msg.text,
                                                isRead: msg.isRead,
                                                createdDateTime: msg.createdDateTime,
                                                oppositeUser: oppositeUser,
                                                oppositeUserName: oppositeUserName,
                                                activeRoom: {
                                                    roomID: activeRoom.roomID,
                                                    receiverID: activeRoom.receiverID,
                                                    channelID: activeRoom.channelID
                                                }
                                            });
                                            
                                            // For now, we only have one user (opposite user in DM)
                                            // This structure can be extended to support multiple users
                                            const readByUsers = oppositeUser ? [{
                                                id: oppositeUser.id || oppositeUser.userID,
                                                name: oppositeUserName,
                                                avatar: oppositeUser.userAvatar
                                            }] : [];
                                            
                                            return (
                                                <>
                                                    <div 
                                                        className="sh-read-receipt-avatar"
                                                        onMouseEnter={() => setHoveredReadReceipt(msg.id)}
                                                        onMouseLeave={() => setHoveredReadReceipt(null)}
                                                    >
                                                        {oppositeUser?.userAvatar ? (
                                                            <img
                                                                src={oppositeUser.userAvatar}
                                                                className="sh-read-receipt-avatar__img"
                                                                alt="read receipt"
                                                            />
                                                        ) : (
                                                            <div
                                                                className="sh-read-receipt-avatar__initials"
                                                                style={{ backgroundColor: getAvatarColor(oppositeUserName) }}
                                                            >
                                                                {getFirstAndLastInitials(oppositeUserName)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Hover overlay - positioned relative to message container */}
                                                    {hoveredReadReceipt === msg.id && readByUsers.length > 0 && (
                                                        <div className="sh-read-receipt-overlay">
                                                            <div className="sh-read-receipt-overlay__header">
                                                                {getTranslation('seenByXPeople', { 
                                                                    count: readByUsers.length,
                                                                    plural: readByUsers.length === 1 ? getTranslation('person') : getTranslation('people')
                                                                })}
                                                            </div>
                                                            <div className="sh-read-receipt-overlay__list">
                                                                {readByUsers.map((user) => (
                                                                    <div key={user.id} className="sh-read-receipt-overlay__item">
                                                                        {user.avatar ? (
                                                                            <img
                                                                                src={user.avatar}
                                                                                className="sh-read-receipt-overlay__avatar"
                                                                                alt={user.name}
                                                                            />
                                                                        ) : (
                                                                            <div
                                                                                className="sh-read-receipt-overlay__avatar sh-read-receipt-overlay__avatar--initials"
                                                                                style={{ backgroundColor: getAvatarColor(user.name) }}
                                                                            >
                                                                                {getFirstAndLastInitials(user.name)}
                                                                            </div>
                                                                        )}
                                                                        <span className="sh-read-receipt-overlay__name">{user.name}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        }
                                        // For channel messages, we could show a generic read indicator
                                        // For now, return null as channel read receipts may need different handling
                                        return null;
                                    })()}
                                </div>
                            </React.Fragment>
                        );
                    });
                })()}
                {typingUser && (
                    <div className="social-hive-chat__message social-hive-chat__message--received">
                        <div className="social-hive-chat__message-row social-hive-chat__message-row--received">
                            {/** Avatar */}
                            {typingUser?.userAvatar
                                ? <img
                                    src={typingUser.userAvatar}
                                    className="social-hive-chat__avatar"
                                    alt="avatar"
                                />
                                : <div
                                    className="social-hive-chat__avatar"
                                    style={{ backgroundColor: getAvatarColor(typingUser?.username) }}
                                >
                                    {getFirstAndLastInitials(typingUser.username)}
                                </div>
                            }

                            {/** Bubble içi animasyon */}
                            <div className="social-hive-chat__bubble typing-indicator">
                                <div className="loading-indicator">
                                    <span><div style={{ width: '4px', height: '4px', borderRadius: "100px" }} /></span>
                                    <span><div style={{ width: '4px', height: '4px', borderRadius: "100px" }} /></span>
                                    <span><div style={{ width: '4px', height: '4px', borderRadius: "100px" }} /></span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Emoji Dialog (simple modal) */}
            {emojiDialogFor && (
                <div className="sh-emoji-dialog" onClick={() => setEmojiDialogFor(null)}>
                    <div className="sh-emoji-dialog__panel" onClick={(e) => e.stopPropagation()}>
                        <div className="sh-emoji-dialog__header">{getTranslation('addReaction')}</div>
                        <div className="sh-emoji-dialog__grid">
                            {PICKER_EMOJIS.map(e => (
                                <button
                                    key={e}
                                    className="sh-emoji-option"
                                    onClick={() => { toggleReactionGlobal(emojiDialogFor, e); setEmojiDialogFor(null); }}
                                >
                                    {e}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {uploadProgress > 0 && (
                <div className="social-hive-chat__upload-progress">
                    <div
                        className="social-hive-chat__upload-progress-bar"
                        style={{ width: `${uploadProgress}%` }}
                    />
                    <span className="social-hive-chat__upload-progress-text">
                        {uploadProgress}%
                    </span>
                </div>
            )}
            {(selectedChannel.channelID !== "0" && activeRoom.canUsersSendMessage === "0" && isAdmin === "0") ?
                <div className="social-hive-chat__input-area-holder">
                    <div className="social-hive-chat__input-area" style={{ paddingLeft: '15px' }}>
                        {getTranslation("onlyAdminsCanSendMessage")}
                    </div>
                </div>
                :

                <div className="social-hive-chat__input-area-holder">
                    {attachments.length >= MAX_FILES && (
                        <div style={{
                            color: 'var(--sh-chat-status-error-color)',
                            fontSize: '12px',
                            fontWeight: '500',
                            marginBottom: '8px',
                            textAlign: 'center'
                        }}>
                            {getTranslation("maxFilesReached", { maxFiles: MAX_FILES })}
                        </div>
                    )}
                    {attachments.length > 0 && (
                        <div className="social-hive-chat__preview-attachments">
                            {attachments.map((file, idx) => (
                                <div key={idx} className="social-hive-chat__preview-attachment">
                                    <div className="social-hive-chat__attachment-content">
                                        {file.type.startsWith('image/')
                                            ? <img src={URL.createObjectURL(file)} alt={file.name} />
                                            : <div className="social-hive-chat__file-icon" style={{ color: "var(--sh-chat-message-font-color)" }}>
                                                {getFileIcon(file.name, file.type)}
                                            </div>
                                        }
                                        <div className="social-hive-chat__attachment-filename">{file.name}</div>
                                    </div>
                                    <button className="social-hive-chat__remove-attachment" onClick={() => removeAttachment(idx)}>
                                        <IoClose size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="social-hive-chat__input-area">
                        <button className="social-hive-chat__add-btn" onClick={handleAddClick}><IoAddCircleSharp size={24} /></button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            style={{ display: 'none' }}
                            multiple
                            onChange={handleFilesSelected}
                        />


                        <textarea
                            ref={textareaRef}
                            className="social-hive-chat__input"
                            placeholder={"Message #" + (activeRoom.titleDetails?.name || activeRoom.title)}
                            value={inputValue}
                            onChange={e => {
                                setInputValue(e.target.value);
                                sendTypingStatusToSocket();
                                autoResizeTextarea();
                            }}
                            onKeyDown={handleKeyDown}
                            rows={1}
                        />

                        <button onClick={sendMessage} className="social-hive-chat__gif-btn"><TbSend2 size={24} /></button>
                    </div>
                </div>
            }

            <AlertBox
                isOpen={isDeleteMessageOpen}
                message={getTranslation("deleteMessageWarning")}
                type={"warning"}
                onConfirm={() => { handleDeleteMessage(deleteMessageID); setIsDeleteMessageOpen(false); setDeleteMessageID(""); }}
                onCancel={() => { setIsDeleteMessageOpen(false); setDeleteMessageID(""); }}
                confirmText={getTranslation("yes")}
                cancelText={getTranslation("no")}
            />

            <AlertBox
                isOpen={isDeleteDMMessageOpen}
                message={getTranslation("deleteMessageWarning") || "Are you sure you want to delete this message? This action cannot be undone."}
                type={"warning"}
                onConfirm={() => { handleDeleteDMMessage(deleteDMMessageID); setIsDeleteDMMessageOpen(false); setDeleteDMMessageID(""); }}
                onCancel={() => { setIsDeleteDMMessageOpen(false); setDeleteDMMessageID(""); }}
                confirmText={getTranslation("yes")}
                cancelText={getTranslation("no")}
            />

            {modalData.isOpen && (
                <Modal
                    fileUrl={modalData.fileUrl}
                    fileName={modalData.fileName}
                    mimeType={modalData.mimeType}
                    messageData={modalData.messageData}
                    gallery={modalData.gallery}
                    initialIndex={modalData.initialIndex}
                    onClose={() => setModalData({ isOpen: false, fileUrl: '', fileName: '', mimeType: '', messageData: null, gallery: null, initialIndex: 0 })}
                />
            )}

            <DragDropOverlay
                isVisible={dragDropVisible}
            />
        </div>
    );
};

export default SocialHiveChat;